import { Metric } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import {
  METRIC_TO_FORMATTER,
  SPEND_OR_REVENUE_FORMATTER,
  toPrettyNumber,
  toPrettyPercent2Decimals
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { AggregatorDatum } from '@blisspointmedia/bpm-types/dist/Metrics';
import { ColumnMetaData, ColumnMetaDataMap } from '@Components/MetricsTable/metrics-table-utils';
import { sharedColumnMetaDataMap } from '@Pages/SingleChannel/Channels/shared-performance-utils';
import { dimensionColumnMetaDataMap as crossChannelDimensionColumnMetaDataMap } from '@Pages/CrossChannel/cross-channel-utils';
import {
  buildKpiToKey,
  meanRatioGoal,
  num,
  pacingHeaderLabel,
  pacingRatioRecipe,
  PacingMetric,
  PacingRatioMetric,
  PACING_RATIO_HIGHER_IS_BETTER,
  ratioOfSums,
  ratioPctToGoal
} from './pacingPresetColumnPicker';

export { buildKpiToKey };

const { SPEND, REVENUE } = Metric;

// Fetch-bucket routing lives in the `dataFetchKey` MetricsTableWithPresets prop (getPacingDataFetchKey)
// and the simple/adjusted resolver (resolvePacingMode) in pacingFetchKeys. Each column below is a
// derived metric: MetricsTable computes it from the base columns it lists in `requiredColumns`, pulled
// from the row's dataFetchKey-selected bucket. The same `aggregator` runs per-cell (datum = the row's
// base cols) and for the totals row (datum = the cross-row sums).
// Goal buckets arrive carrying mode-resolved keys { toward, goal, projected, run_rate, yesterday }.
// `goal_rows` is 1 per goal-bearing row so the mean-over-goal-bearing-rows totals (run rate, ratio
// goal/% to goal) divide by the right count. On additive buckets it is mode-resolved too (the lambda
// ships simple_/adjusted_goal_rows, since a goal can be booked in one mode but not the other); the
// ratio buckets ship a flat goal_rows = 1.

// ── Per-variant goal column factory ──────────────────────────────────────────────
// Each goal-type variant repeats the same 7 columns over its bucket. dataVarNames are metric-prefixed
// so the per-column totals accumulators never collide. Goal-relative columns key off the attributed
// "toward" value (not Total-X); ratios are FE-derived per-cell AND for the totals row.
interface GoalVariant {
  prefix: PacingMetric; // "spend" | "revenue" | "volume"
  metricLabel: string; // "Spend" | "Revenue" | "KPI Volume" — used in overlay copy
  formatter: ColumnMetaData['formatter'];
}

const makeGoalVariantMeta = ({
  prefix,
  metricLabel,
  formatter
}: GoalVariant): Record<string, ColumnMetaData> => ({
  [`${prefix}_toward_goal`]: {
    prettyName: pacingHeaderLabel(prefix, 'toward_goal'),
    formatter,
    aggregator: (datum: AggregatorDatum) => num(datum.toward),
    requiredColumns: ['toward'],
    overlayText: `The amount of ${metricLabel} that was able to be attributed to a goal in goal intake. This can differ from the actual ${metricLabel} column if your goal does not cover the entire date range you have selected.`
  },
  [`${prefix}_projected`]: {
    prettyName: pacingHeaderLabel(prefix, 'projected'),
    formatter,
    aggregator: (datum: AggregatorDatum) => num(datum.projected),
    requiredColumns: ['projected'],
    overlayText: ''
  },
  [`${prefix}_goal`]: {
    prettyName: pacingHeaderLabel(prefix, 'goal'),
    formatter,
    aggregator: (datum: AggregatorDatum) => num(datum.goal),
    requiredColumns: ['goal'],
    overlayText: `The ${metricLabel} goal that was input into goal intake. In the case that you have selected a subset of the entire goal date range we are prorating the goal amount to match the number of days selected.`
  },
  [`${prefix}_pct_to_goal`]: {
    prettyName: pacingHeaderLabel(prefix, 'pct_to_goal'),
    formatter: toPrettyPercent2Decimals,
    // Percent columns default to 2 decimals; surface that as the modal placeholder.
    decimals: 2,
    aggregator: (datum: AggregatorDatum) => {
      const goal = num(datum.goal);
      return goal > 0 ? num(datum.toward) / goal : null;
    },
    requiredColumns: ['toward', 'goal'],
    overlayText:
      'Your actual performance divided by your goal amount. The percent of the goal that you have already accomplished.'
  },
  [`${prefix}_pacing`]: {
    prettyName: pacingHeaderLabel(prefix, 'pacing'),
    formatter: toPrettyPercent2Decimals,
    // Percent columns default to 2 decimals; surface that as the modal placeholder.
    decimals: 2,
    aggregator: (datum: AggregatorDatum) => {
      const goal = num(datum.goal);
      return goal > 0 ? num(datum.projected) / goal : null;
    },
    requiredColumns: ['projected', 'goal'],
    overlayText:
      'Takes the daily average performance and stretches that across the remaining days in the goal to determine what you are currently pacing to for the goal value for the date range selected.'
  },
  [`${prefix}_run_rate`]: {
    prettyName: pacingHeaderLabel(prefix, 'run_rate'),
    formatter,
    // Per cell: the row's own required rate (the raw value — 0 for a row with no goal in this mode).
    // The totals row (goal_col_rows > 1, i.e. it aggregates more than one row) means the rate over the
    // goal-bearing rows so rows without a goal don't dilute it, and renders "--" when none are
    // goal-bearing. `goal_rows` is the mode-resolved count of goal-bearing rows; `goal_col_rows` is the
    // always-1-per-cell row count that tells the totals row apart from a single no-goal cell (both of
    // which have goal_rows 0), so a zero-goal Ideal total shows "--" rather than $0.00.
    aggregator: (datum: AggregatorDatum) => {
      const goalRows = num(datum.goal_rows);
      if (num(datum.goal_col_rows) > 1) {
        return goalRows > 0 ? num(datum.run_rate) / goalRows : null;
      }
      return num(datum.run_rate);
    },
    requiredColumns: ['run_rate', 'goal_rows', 'goal_col_rows'],
    overlayText: `The amount of ${metricLabel} that you need to achieve on a daily basis moving forward to hit your goal.`
  },
  [`${prefix}_remainder`]: {
    prettyName: pacingHeaderLabel(prefix, 'remainder'),
    formatter,
    // Clamp at 0 — once the goal is met/exceeded there's nothing remaining (no negative remainder).
    aggregator: (datum: AggregatorDatum) => Math.max(0, num(datum.goal) - num(datum.toward)),
    requiredColumns: ['goal', 'toward'],
    overlayText:
      'Goal total subtracted by the actual result to show the remainder amount for the time period.'
  },
  [`${prefix}_yesterday`]: {
    prettyName: pacingHeaderLabel(prefix, 'yesterday'),
    formatter,
    aggregator: (datum: AggregatorDatum) => num(datum.yesterday),
    requiredColumns: ['yesterday'],
    overlayText: `The amount of ${metricLabel} attributed to a goal yesterday.`
  }
});

// ── Per-ratio-metric goal column factory (Actual / Towards Goal / Goal / % to Goal) ─
// Mirrors the additive shape. Actual is the raw ratio-of-sums over the full selected range; Towards
// Goal is the ratio over the goal window only (the lambda ships those component sums under a
// `toward_` prefix); Goal is the flat target; % to Goal compares Towards Goal against the goal. The
// totals row is a ratio-of-sums (components summed) and MEANS the goal over goal-bearing rows.
const makeRatioGoalVariantMeta = (metric: PacingRatioMetric): Record<string, ColumnMetaData> => {
  const formatter = METRIC_TO_FORMATTER[metric];
  const label = metric.toUpperCase();
  const higherBetter = PACING_RATIO_HIGHER_IS_BETTER[metric];
  const { numerator, denominator } = pacingRatioRecipe(metric);
  const towardNum = `toward_${numerator}`;
  const towardDen = `toward_${denominator}`;
  return {
    [metric]: {
      prettyName: pacingHeaderLabel(metric, 'actual'),
      formatter,
      // Ratio columns render 2 decimals; surface that as the modal placeholder.
      decimals: 2,
      // Raw ratio over the whole selected range (not goal-scoped).
      aggregator: (datum: AggregatorDatum) => ratioOfSums(datum, metric),
      requiredColumns: [numerator, denominator],
      overlayText: `The actual ${label} performance from the time period selected.`
    },
    [`${metric}_toward_goal`]: {
      prettyName: pacingHeaderLabel(metric, 'toward_goal'),
      formatter,
      decimals: 2,
      // Ratio over the goal window only; null when no goal covers the row.
      aggregator: (datum: AggregatorDatum) => ratioOfSums(datum, metric, 'toward_'),
      requiredColumns: [towardNum, towardDen],
      overlayText: `The ${label} over the portion of the date range covered by a goal.`
    },
    [`${metric}_goal`]: {
      prettyName: pacingHeaderLabel(metric, 'goal'),
      formatter,
      decimals: 2,
      // Mean of the flat target over goal-bearing rows; null when no goal matches the row.
      aggregator: (datum: AggregatorDatum) => meanRatioGoal(datum),
      requiredColumns: ['goal', 'goal_rows'],
      overlayText: `The ${label} goal that was input into goal intake.`
    },
    [`${metric}_pct_to_goal`]: {
      prettyName: pacingHeaderLabel(metric, 'pct_to_goal'),
      formatter: toPrettyPercent2Decimals,
      decimals: 2,
      // Compares Towards Goal (goal-window ratio) against the goal, direction-aware.
      aggregator: (datum: AggregatorDatum) =>
        ratioPctToGoal(metric, ratioOfSums(datum, metric, 'toward_'), meanRatioGoal(datum) ?? 0),
      requiredColumns: [towardNum, towardDen, 'goal', 'goal_rows'],
      overlayText: higherBetter
        ? `Your Towards Goal ${label} divided by your ${label} goal — over 100% means you are beating the goal.`
        : `Your ${label} goal divided by your Towards Goal ${label} — over 100% means you are beating the goal.`
    }
  };
};

// ── Base delivery + active-KPI performance columns (Total-X + derived ratios) ──────
const pacingSpecificColumnMetaData: Record<string, ColumnMetaData> = {
  spend: {
    ...sharedColumnMetaDataMap[SPEND],
    prettyName: pacingHeaderLabel('spend', 'actual'),
    overlayText: 'The actual Spend performance from the time period selected.'
  },
  revenue: {
    ...sharedColumnMetaDataMap[REVENUE],
    prettyName: pacingHeaderLabel('revenue', 'actual'),
    overlayText: 'The actual Revenue performance from the time period selected.'
  },
  kpi_volume: {
    prettyName: pacingHeaderLabel('volume', 'actual'),
    formatter: toPrettyNumber,
    overlayText: 'The actual KPI Volume performance from the time period selected.'
  },
  // Delivery-only actuals (no goal) — read straight off the shared delivery bucket.
  impressions: {
    prettyName: pacingHeaderLabel('impressions', 'actual'),
    formatter: toPrettyNumber,
    overlayText: 'The actual Impressions delivered in the time period selected.'
  },
  clicks: {
    prettyName: pacingHeaderLabel('clicks', 'actual'),
    formatter: toPrettyNumber,
    overlayText: 'The actual Clicks delivered in the time period selected.'
  },
  ...makeGoalVariantMeta({
    prefix: 'spend',
    metricLabel: 'Spend',
    formatter: SPEND_OR_REVENUE_FORMATTER
  }),
  ...makeGoalVariantMeta({
    prefix: 'revenue',
    metricLabel: 'Revenue',
    formatter: SPEND_OR_REVENUE_FORMATTER
  }),
  ...makeGoalVariantMeta({
    prefix: 'volume',
    metricLabel: 'KPI Volume',
    formatter: toPrettyNumber
  }),
  ...makeRatioGoalVariantMeta('roas'),
  ...makeRatioGoalVariantMeta('cpx'),
  ...makeRatioGoalVariantMeta('cpc'),
  ...makeRatioGoalVariantMeta('cpm'),
  ...makeRatioGoalVariantMeta('ctr')
};

// Metric-only map: used by EditTablePresetModal to list configurable data columns. Contains the 3
// standard Total-X columns (Total Spend / KPI Revenue / KPI Volume), the Impressions / Clicks
// delivery actuals, the 3 additive goal variants, and the ratio metrics (ROAS/CPX/CPC/CPM/CTR) with
// their Actual / Goal / % to Goal columns. Conversion-rate metrics are not included.
export const pacingMetricColumnMetaDataMap: ColumnMetaDataMap = {
  ...pacingSpecificColumnMetaData
};

// Dimension-only map for EditTablePresetModal's grouping dropdown. Date is omitted — it's never a
// user-chosen grouping for pacing (summary buckets by dimension combos; over-time auto-locks Date as
// the left column).
const pacingDimsWithoutDate = { ...crossChannelDimensionColumnMetaDataMap } as Record<
  string,
  ColumnMetaData
>;
delete pacingDimsWithoutDate.Date;
delete pacingDimsWithoutDate.ComparisonDate;
export const pacingDimensionColumnMetaDataMap: ColumnMetaDataMap = pacingDimsWithoutDate;
